import { digitsOf, isValidCpf } from '@hue/contracts';
import { DocumentInput } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { clientsApi, getApiError } from '../lib/api.js';
import { RequestAlert } from './data-view.js';

/// Busca exata e autorizada no cadastro da empresa. Não transforma um CPF em busca por telefone.
export function ClientCpfSearch({
  onSelect,
  disabled = false,
  label = 'Buscar cliente por CPF',
  includeArchived = false,
  actionLabel = 'Selecionar',
}: {
  onSelect: (id: string, name: string) => void;
  disabled?: boolean;
  label?: string;
  includeArchived?: boolean;
  actionLabel?: string;
}) {
  const [value, setValue] = useState('');
  const cpf = digitsOf(value) ?? '';
  const valid = isValidCpf(cpf);
  const result = useQuery({
    queryKey: ['company', 'clients', 'cpf', cpf, includeArchived],
    queryFn: () =>
      clientsApi.list({
        cpf,
        activity: includeArchived ? 'ALL' : 'ACTIVE',
        kind: 'PERSON',
        access: 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 1,
        offset: 0,
      }),
    enabled: valid && !disabled,
    retry: false,
  });
  const client = valid ? result.data?.clients[0] : undefined;
  return (
    <div>
      <DocumentInput
        kind="CPF"
        label={label}
        value={value}
        onValueChange={(next) => setValue(next ?? '')}
        disabled={disabled}
        error={cpf && !valid ? 'Informe um CPF válido com 11 dígitos.' : undefined}
        hint="A consulta acontece somente após preencher um CPF válido."
      />
      {valid && !disabled ? (
        result.isFetching ? (
          <p role="status" className="mt-2 text-sm text-content-muted">
            Buscando cliente…
          </p>
        ) : result.error ? (
          <RequestAlert message={getApiError(result.error)} />
        ) : client ? (
          <button
            type="button"
            className="mt-2 cursor-pointer rounded-lg border border-surface-border px-3 py-2 text-sm text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            onClick={() => {
              onSelect(client.id, client.displayName);
              setValue('');
            }}
          >
            {actionLabel} {client.displayName}
          </button>
        ) : result.data ? (
          <p role="status" className="mt-2 text-sm text-content-muted">
            {includeArchived
              ? 'Nenhum cliente com este CPF.'
              : 'Nenhum cliente ativo com este CPF.'}
          </p>
        ) : null
      ) : null}
    </div>
  );
}
